type PostHogClient = typeof import("posthog-js").default;

type FeatureFlagListener = () => void;
type EventPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | number[];
type EventProperties = Record<string, EventPropertyValue>;
type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal";
type LogAttributeValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | unknown[]
  | Record<string, unknown>;
type LogAttributes = Record<string, LogAttributeValue>;

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = "https://us.i.posthog.com";
const POSTHOG_CAPTURE_CONSOLE_LOGS =
  import.meta.env.VITE_POSTHOG_CAPTURE_CONSOLE_LOGS !== "false";
const POSTHOG_SERVICE_VERSION = import.meta.env.VITE_APP_VERSION;
const isDev = import.meta.env.DEV;

let posthogClient: PostHogClient | null = null;
let initPromise: Promise<PostHogClient | null> | null = null;
let initialized = false;
let flagsLoaded = false;

const featureFlagListeners = new Set<FeatureFlagListener>();

const notifyFeatureFlagListeners = () => {
  for (const listener of featureFlagListeners) {
    listener();
  }
};

const isBrowser = (): boolean => typeof window !== "undefined";

const cleanLogAttributes = (attributes: LogAttributes): LogAttributes =>
  Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => value !== undefined && value !== null)
  ) as LogAttributes;

const getTenantLogAttributes = (): LogAttributes => {
  if (!isBrowser()) return {};

  const tenant = window.__WAYPOINT_TENANT_CONFIG__;
  return cleanLogAttributes({
    "waypoint.tenant_id": tenant?.id,
    "waypoint.client_slug": tenant?.clientSlug,
    "url.hostname": window.location.hostname,
    "url.host": window.location.host,
  });
};

const getLogsConfig = () => ({
  serviceName: "waypoint-frontend",
  environment: import.meta.env.MODE,
  ...(POSTHOG_SERVICE_VERSION ? { serviceVersion: POSTHOG_SERVICE_VERSION } : {}),
  captureConsoleLogs: POSTHOG_CAPTURE_CONSOLE_LOGS,
  flushIntervalMs: 3000,
  maxBufferSize: 100,
  maxLogsPerInterval: 1000,
  resourceAttributes: getTenantLogAttributes(),
});

const captureInitializedLog = (posthog: PostHogClient): void => {
  try {
    posthog.logger.info(
      "waypoint frontend posthog initialized",
      cleanLogAttributes({
        ...getTenantLogAttributes(),
        "posthog.host": POSTHOG_HOST,
        "posthog.capture_console_logs": POSTHOG_CAPTURE_CONSOLE_LOGS,
      })
    );
  } catch {
    // Keep log setup failures from blocking analytics initialization.
  }
};

export const hasPostHogKey = Boolean(POSTHOG_KEY);

export const getPostHogClient = (): PostHogClient | null => posthogClient;

export const isPostHogInitialized = (): boolean => initialized;

export const areFeatureFlagsLoaded = (): boolean => !POSTHOG_KEY || flagsLoaded;

export const subscribeToFeatureFlags = (listener: FeatureFlagListener): (() => void) => {
  featureFlagListeners.add(listener);
  return () => {
    featureFlagListeners.delete(listener);
  };
};

export const initPostHog = (): void => {
  if (initialized || initPromise) return;

  if (!POSTHOG_KEY) {
    if (isDev) console.warn("[PostHog] VITE_POSTHOG_KEY not set, feature flags disabled");
    return;
  }

  initPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthogClient = posthog;

      if (initialized) return posthogClient;

      if (isDev) {
        console.info(
          "[PostHog] Initializing with key:",
          `${POSTHOG_KEY.slice(0, 10)}...`,
          "host:",
          POSTHOG_HOST
        );
      }

      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        capture_dead_clicks: false,
        capture_exceptions: false,
        capture_heatmaps: false,
        capture_performance: false,
        logs: getLogsConfig(),
        disable_conversations: true,
        disable_product_tours: true,
        disable_session_recording: true,
        disable_surveys: true,
        disable_surveys_automatic_display: true,
        __preview_deferred_init_extensions: true,
        bootstrap: {},
        loaded: (ph) => {
          posthogClient = ph as PostHogClient;
          ph.onFeatureFlags((flags) => {
            flagsLoaded = true;
            if (isDev) console.info("[PostHog] Feature flags received:", flags);
            notifyFeatureFlagListeners();
          });
          ph.reloadFeatureFlags();
          captureInitializedLog(ph as PostHogClient);
        },
      });

      initialized = true;
      notifyFeatureFlagListeners();
      return posthogClient;
    })
    .catch((error) => {
      initPromise = null;
      if (isDev) console.warn("[PostHog] Failed to initialize", error);
      return null;
    });
};

export const ensurePostHog = (): Promise<PostHogClient | null> => {
  initPostHog();
  return initPromise ?? Promise.resolve(posthogClient);
};

export const capturePostHogEvent = (
  eventName: string,
  properties?: EventProperties
): void => {
  void ensurePostHog()
    .then((posthog) => {
      posthog?.capture(eventName, properties);
    })
    .catch(() => {
      // Keep analytics failures from affecting customer flows.
    });
};

export const capturePostHogLog = (
  level: LogLevel,
  body: string,
  attributes?: LogAttributes
): void => {
  void ensurePostHog()
    .then((posthog) => {
      posthog?.logger[level](
        body,
        cleanLogAttributes({
          ...getTenantLogAttributes(),
          ...(attributes ?? {}),
        })
      );
    })
    .catch(() => {
      // Keep logging failures from affecting customer flows.
    });
};

export const identifyPostHogUser = (
  userId: string,
  properties?: Record<string, unknown>
): void => {
  void ensurePostHog()
    .then((posthog) => {
      posthog?.identify(userId, properties);
    })
    .catch(() => undefined);
};

export const resetPostHogUser = (): void => {
  void ensurePostHog()
    .then((posthog) => {
      posthog?.reset();
    })
    .catch(() => undefined);
};
